import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Fingerprint, AlertCircle } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/services/auth.service';
import { Button } from '@/components/ui/button';

type State = 'exchanging' | 'success' | 'failed';

export default function SsoExchangePage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const setSession = useAuthStore(s => s.setSession);
    const [state, setState] = useState<State>('exchanging');
    const [errorMsg, setErrorMsg] = useState<string>('');

    useEffect(() => {
        const token = params.get('token');
        if (!token) {
            setState('failed');
            setErrorMsg(t('auth.sso.failed'));
            return;
        }
        (async () => {
            try {
                const res = await authService.ssoExchange({ token });
                const data: any = res.data;
                setSession({
                    user: data.user,
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                });
                setState('success');
                // Bersihkan URL dari token (history)
                window.history.replaceState({}, '', '/');
                navigate('/', { replace: true });
            } catch (err: any) {
                setState('failed');
                setErrorMsg(err?.response?.data?.error || t('auth.sso.failed'));
            }
        })();
    }, []);

    return (
        <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-gradient-to-b from-brand to-brand-700 text-white px-6">
            <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
                {state === 'failed' ? <AlertCircle className="h-8 w-8" /> : <Fingerprint className="h-8 w-8" />}
            </div>
            {state === 'exchanging' && (
                <>
                    <p className="text-base font-bold">{t('auth.sso.exchanging')}</p>
                    <div className="mt-4 h-1 w-32 rounded-full bg-white/20 overflow-hidden">
                        <div className="h-full w-1/2 bg-white animate-pulse" />
                    </div>
                </>
            )}
            {state === 'failed' && (
                <>
                    <p className="text-base font-bold mb-2">{errorMsg}</p>
                    <Button variant="secondary" onClick={() => navigate('/login', { replace: true })}>
                        {t('auth.login_btn')}
                    </Button>
                </>
            )}
        </div>
    );
}
